import { LengthCode } from '@icommerce/units';
import type { ProductAttribute, ProductAttributeValueAmountUnit } from '@libs/attribute.helper';
import { getAttributeValue } from '@libs/attribute.helper';
import type { CfCaseProductBoxProps } from '@libs/cfCase.helper';
import type { AssociationProductFragmentEnhanced, ProductFragmentEnhanced } from '@models/enhanced-fragments';
import type { Measurement } from '@models/graphql';
import { getCfCaseProductBoxProps } from '@product/ProductPage/PageLayouts/getCfCaseProductBoxProps';
import { getCfCaseProducts } from '@product/ProductPage/PageLayouts/getCfCaseProducts';

export interface ExtractCFCaseProps {
  cfCases: CfCaseProductBoxProps[];
  cfCaseLengths: Record<string, Measurement<number>>;
}

export const extractCFCaseProps = (
  product: ProductFragmentEnhanced,
  associationProducts: Record<string, AssociationProductFragmentEnhanced>
): ExtractCFCaseProps => {
  const cfCases: CfCaseProductBoxProps[] = [];
  const cfCaseLengths: Record<string, Measurement<number>> = {};

  // cfCaseLength containing max and min used to decide if or not add the cfcase to shopping cart
  const cfCaseProducts = getCfCaseProducts(product, associationProducts) || [];
  if (cfCaseProducts.length <= 0) {
    // continue with next product if the current product does not have any cfCase products
    return { cfCases: [], cfCaseLengths: {} };
  }

  for (const cfCaseProduct of cfCaseProducts) {
    const cfCaseProductBoxProps = getCfCaseProductBoxProps(cfCaseProduct, undefined);
    cfCases.push(cfCaseProductBoxProps);
    const caseLengthMaxAttributeName =
      cfCaseProductBoxProps.caseCode === 'M'
        ? `CFCase_length_max_metric`
        : `CFCase_${cfCaseProductBoxProps.caseCode}_length_max_metric`;
    const caseLengthMinAttributeName =
      cfCaseProductBoxProps.caseCode === 'M'
        ? `CFCase_length_min_metric`
        : `CFCase_${cfCaseProductBoxProps.caseCode}_length_min_metric`;
    const maxLengthAttribute = getAttributeValue<ProductAttributeValueAmountUnit>(
      product?.attributes?.[caseLengthMaxAttributeName] as ProductAttribute<ProductAttributeValueAmountUnit>
    );
    let minLengthAttribute = getAttributeValue<ProductAttributeValueAmountUnit>(
      product.attributes?.[caseLengthMinAttributeName] as ProductAttribute<ProductAttributeValueAmountUnit>
    );
    // Default MIN value for CFCASE S is 0
    if (!minLengthAttribute && cfCaseProductBoxProps.caseCode === 'S') {
      minLengthAttribute = { amount: 0, unit: LengthCode.METER } as ProductAttributeValueAmountUnit;
    }
    if (minLengthAttribute) {
      cfCaseLengths[`minCase${cfCaseProductBoxProps.caseCode}`] = {
        value: minLengthAttribute?.amount,
        unit: minLengthAttribute?.unit,
      };
    }
    if (maxLengthAttribute) {
      cfCaseLengths[`maxCase${cfCaseProductBoxProps.caseCode}`] = {
        value: maxLengthAttribute?.amount,
        unit: maxLengthAttribute?.unit,
      };
    }
  }

  cfCases.sort((a, b) => (a.sorting < b.sorting ? -1 : 1));
  return { cfCases, cfCaseLengths };
};
